'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, BarChart3, ArrowUpRight, Users, DollarSign, TrendingUp, CreditCard, Smartphone, CheckCircle, AlertCircle } from 'lucide-react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { formatNumber, formatCurrency, cn } from '@/lib/utils';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalNumbers: number;
  assignedNumbers: number;
  totalSmsReceived: number;
  totalVoiceMinutes: number;
  totalRewardsPaid: number;
  totalRevenue: number;
  totalWithdrawals: number;
  pendingWithdrawals: number;
  activeProviders: number;
  healthyProviders: number;
  systemUptime: number;
}

interface Provider {
  id: string;
  displayName: string;
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  uptime: number;
  latency: number;
  errorRate: number;
  messagesToday: number;
  costToday: number;
}

const statCards = [
  { name: 'Total Users', value: 0, icon: Users, color: 'text-cyan-400', bg: 'bg-cyan-500/10', link: '/admin/users' },
  { name: 'Active Users', value: 0, icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10', link: '/admin/users' },
  { name: 'Total Numbers', value: 0, icon: Smartphone, color: 'text-purple-400', bg: 'bg-purple-500/10', link: '/admin/numbers' },
  { name: 'Assigned Numbers', value: 0, icon: CheckCircle, color: 'text-blue-400', bg: 'bg-blue-500/10', link: '/admin/numbers' },
];

const revenueCards = [
  { name: 'Total Rewards Paid', value: '$0.00', icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10' },
  { name: 'Total Revenue', value: '$0.00', icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  { name: 'Total Withdrawals', value: '$0.00', icon: CreditCard, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { name: 'Pending Withdrawals', value: '$0.00', icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
];

export function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, providersRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getProviders(`limit=20`),
      ]);
      if (statsRes) setStats(statsRes as any);
      if (providersRes) setProviders((providersRes as any).items || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-500/10 text-green-400">Healthy</Badge>;
      case 'degraded': return <Badge className="bg-amber-500/10 text-amber-400">Degraded</Badge>;
      case 'unhealthy': return <Badge className="bg-red-500/10 text-red-400">Unhealthy</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="glass">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-1/4 mb-2" />
                <Skeleton className="h-8 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const cardsData = stats ? [
    { ...statCards[0], value: stats.totalUsers },
    { ...statCards[1], value: stats.activeUsers },
    { ...statCards[2], value: stats.totalNumbers },
    { ...statCards[3], value: stats.assignedNumbers },
  ] : statCards;

  const revenueData = stats ? [
    { ...revenueCards[0], value: formatCurrency(stats.totalRewardsPaid) },
    { ...revenueCards[1], value: formatCurrency(stats.totalRevenue) },
    { ...revenueCards[2], value: formatCurrency(stats.totalWithdrawals) },
    { ...revenueCards[3], value: formatCurrency(stats.pendingWithdrawals) },
  ] : revenueCards;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
          <p className="text-slate-400 mt-1">System overview and key metrics</p>
        </div>
        <Button variant="outline" onClick={fetchDashboardData} disabled={loading}>
          <Activity className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cardsData.map((stat, i) => (
          <Link key={i} to={stat.link} className="glass card-hover">
            <Card className="h-full">
              <CardContent className="pt-6 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{stat.name}</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatNumber(stat.value)}</p>
                  </div>
                  <div className={cn('p-3 rounded-xl', stat.bg)}>
                    <stat.icon className={cn('h-6 w-6', stat.color)} />
                  </div>
                </div>
                <ArrowUpRight className="mt-auto text-slate-500 h-4 w-4" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {revenueData.map((card, i) => (
          <Card key={i} className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">{card.name}</p>
                  <p className="text-2xl font-bold text-white mt-1">{card.value}</p>
                </div>
                <div className={cn('p-3 rounded-xl', card.bg)}>
                  <card.icon className={cn('h-6 w-6', card.color)} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Provider Health</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin/providers">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uptime</TableHead>
                  <TableHead>Latency</TableHead>
                  <TableHead>Error Rate</TableHead>
                  <TableHead>Messages Today</TableHead>
                  <TableHead>Cost Today</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {providers.length > 0 ? (
                  providers.slice(0, 10).map(provider => (
                    <TableRow key={provider.id}>
                      <TableCell className="font-medium">{provider.displayName}</TableCell>
                      <TableCell>{getStatusBadge(provider.status)}</TableCell>
                      <TableCell>{(provider.uptime * 100).toFixed(2)}%</TableCell>
                      <TableCell>{provider.latency}ms</TableCell>
                      <TableCell className={cn(provider.errorRate > 0.05 && 'text-red-400')}>
                        {(provider.errorRate * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell>{formatNumber(provider.messagesToday)}</TableCell>
                      <TableCell>${provider.costToday.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-400">No providers found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle>System Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-400">Uptime</p>
                <p className="text-2xl font-bold text-green-400">{(stats?.systemUptime || 0).toFixed(2)}%</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-400">Active Providers</p>
                <p className="text-2xl font-bold text-white">{stats?.activeProviders || 0} / {stats?.healthyProviders || 0} healthy</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-400">SMS Today</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats?.totalSmsReceived || 0)}</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-400">Voice Minutes</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats?.totalVoiceMinutes || 0)}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Provider Health</span>
                <span className="text-sm font-medium">
                  {providers.filter(p => p.status === 'healthy').length} / {providers.length} Healthy
                </span>
              </div>
              <Progress value={providers.length > 0 ? (providers.filter(p => p.status === 'healthy').length / providers.length) * 100 : 0} className="h-2" />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">System Uptime</span>
                <span className="text-sm font-medium text-green-400">{(stats?.systemUptime || 0).toFixed(2)}%</span>
              </div>
              <Progress value={stats?.systemUptime || 0} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-400">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-slate-600" />
            <p>Activity feed would be displayed here</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}